import {
  NavLink,
  Route,
  Routes,
  matchPath,
  useLocation,
  useParams,
} from "react-router";

import { NotificationIcon } from "@citrine/atoms/icons";
import { Spacer } from "@citrine/atoms/spacer";
import { ProjectClient } from "@citrine/resources/project";
import { TeamClient } from "@citrine/resources/team";
import type { DataFullstoryId } from "@citrine/types/full-story";

import { Logo } from "#src/components/logo";

import { TeamInfo } from "../team-info";

import * as sx from "./navbar.sx";
import { RoutedProjectToolbar } from "./project-toolbar";
import { VersionInfo } from "./version-info";

export interface NavRoute {
  "data-fullstoryid"?: DataFullstoryId;
  path: string;
  label: string;
  notification?: boolean;
}

interface NavbarShellProps {
  logo?: {
    alt?: string;
    src?: string;
  };
  children?: React.ReactNode;
}

interface NavbarProps extends NavbarShellProps {
  routes: NavRoute[];
}

export function NavbarShell({ children, logo }: NavbarShellProps) {
  return (
    <sx.Toolbar>
      <sx.Logo to="/">
        <Logo {...logo} />
      </sx.Logo>
      {children}
    </sx.Toolbar>
  );
}

function NavToolbar({ children, logo, routes }: NavbarProps) {
  const loc = useLocation();
  const matchedRoute = routes
    .map((route) =>
      matchPath(route.path.replace(/(^|[^/])\/*$/, "$1/*"), loc.pathname),
    )
    .find(Boolean);

  return (
    <NavbarShell logo={logo}>
      <VersionInfo />
      <RoutedNavbarTeamInfo />
      {/* use negative margin to avoid a double gap in the flex row */}
      <Spacer sx={{ marginLeft: -4 }} />
      <sx.Tabs value={matchedRoute?.pathnameBase ?? false}>
        {routes.map((route: NavRoute) => (
          <sx.Tab
            data-fullstoryid={route["data-fullstoryid"]}
            disableRipple
            key={route.path}
            value={route.path}
            label={
              <sx.Label>
                {route.notification ? (
                  <NotificationIcon fontSize="small" />
                ) : null}
                {route.label}
              </sx.Label>
            }
            component={NavLink}
            to={route.path}
          />
        ))}
      </sx.Tabs>
      {children}
    </NavbarShell>
  );
}

export function Navbar(props: NavbarProps) {
  return (
    <>
      <sx.Navbar role="banner">
        <NavToolbar {...props} />
      </sx.Navbar>
      <sx.ProjectToolbar position="static" role="navigation">
        <RoutedProjectToolbar />
      </sx.ProjectToolbar>
    </>
  );
}

function RoutedNavbarTeamInfo() {
  return (
    <Routes>
      <Route path="data-manager/teams/:teamId">
        <Route index path="*" element={<NavbarTeamInfo />} />
      </Route>
      <Route path="projects/:projectId">
        <Route index path="*" element={<NavbarTeamInfo />} />
      </Route>
      {/* ignore other paths; this will suppress test warnings */}
      <Route index path="*" element={null} />
    </Routes>
  );
}

function NavbarTeamInfo() {
  const { projectId, teamId } = useParams<{
    projectId?: string;
    teamId?: string;
  }>();
  const projectQuery = ProjectClient.getInstance().useGet(projectId);
  const teamQuery = TeamClient.getInstance().useGet(teamId);
  return <TeamInfo team={teamQuery.data ?? projectQuery.data?.team} />;
}
